import { forwardRef, useImperativeHandle, useRef, useEffect } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css';
import type { MapStyle } from './MapControlsFAB';
import { useMapInitialization } from './hooks/useMapInitialization';
import { useMapLayers } from './hooks/useMapLayers';
import { useMapOverlays } from './hooks/useMapOverlays';
import { useMapInteractions } from './hooks/useMapInteractions';
import { useTrafficLayer } from './hooks/useTrafficLayer';
import { useLiveTrafficLayer } from './hooks/useLiveTrafficLayer';
import { useFloodExposureLayer } from './hooks/useFloodExposureLayer';
import { useFloodExposureYearlyLayer } from './hooks/useFloodExposureYearlyLayer';
import { useObservedWeatherLayers } from './hooks/useObservedWeatherLayers';
import { useForecastWeatherLayers } from './hooks/useForecastWeatherLayers';
import { useMotorwayJunctionLayer } from './hooks/useMotorwayJunctionLayer';
import { useCoordalCatalogIcons } from './icons/useCoordalCatalogIcons';
import { DEFAULT_LAYER_SETTINGS, type LayerSettings } from './LayerSettingsPanel';
import { useLayerSettingsSync } from './hooks/useLayerSettingsSync';
import { MAP_MAX_ZOOM, SURVEY_MIN_ZOOM } from './config/mapConfig';
import { applySurveyMinZoom } from '../../utils/mapZoomLock';
import { runWhenMapStyleReady } from '../../utils/mapLayerPromotion';
import {
  enterDrawDirectSelect,
  replaceDrawGeometry,
  trashDraw,
  undoDrawFirstVertex,
  undoDrawLastVertex,
} from './drawControlUtils';
import type { SnapConnectionRecord } from '../../features/geometry-editing/roadEndpointSnap';
import type { TraceCaptureMode } from '../../utils/fieldMappingGps';

/** Coordal Standard uses Mapbox style slots; lines in the default stack sit under 3D buildings at pitch. */
const OVERLAY_LINE_SLOT = 'top';
const OVERLAY_FILL_SLOT = 'middle';
const OVERLAY_POINT_SLOT = 'top';

const overlayLineLayout = { 'line-join': 'round' as const, 'line-cap': 'round' as const };

/** Mapbox Standard 3D line lift — not yet in published mapbox-gl types. */
const overlayLinePaint = (paint: Record<string, unknown>): mapboxgl.LinePaint =>
  paint as mapboxgl.LinePaint;

function extendBoundsFromCoords(bounds: mapboxgl.LngLatBounds, coords: unknown): void {
  if (!Array.isArray(coords) || coords.length === 0) return;
  if (typeof (coords as number[])[0] === 'number') {
    const pair = coords as [number, number];
    bounds.extend(pair);
    return;
  }
  for (const c of coords as unknown[]) {
    extendBoundsFromCoords(bounds, c);
  }
}

function extendBoundsFromGeometry(bounds: mapboxgl.LngLatBounds, geom: GeoJSON.Geometry): void {
  if (geom.type === 'GeometryCollection') return;
  extendBoundsFromCoords(bounds, (geom as { coordinates: unknown }).coordinates);
}

export interface MapContainerRef {
  setStyle: (style: MapStyle) => void;
  toggle3D: (enabled: boolean) => void;
  toggleTerrain: (enabled: boolean, exaggeration?: number) => void;
  setTerrainExaggeration: (exaggeration: number) => void;
  toggleLayer: (layer: string, enabled: boolean) => void;
  applyBuildingLayers: (buildings: boolean, buildings3D: boolean) => void;
  getMap: () => import('mapbox-gl').Map | null;
  getZoom: () => number;
  getCenter: () => { lng: number; lat: number };
  zoomIn: () => void;
  zoomOut: () => void;
  resetNorth: () => void;
  resetPitch: () => void;
  toggleTilt: (tilted: boolean) => void;
  setPitch: (pitch: number) => void;
  getBearing: () => number;
  getPitch: () => number;
  flyTo: (
    center: { lng: number; lat: number },
    zoom: number,
    options?: { bottomPadding?: number },
  ) => void;
  lockZoom: (zoom?: number) => void;
  unlockZoom: () => void;
  drawTrash: () => boolean;
  drawUndo: () => boolean;
  drawUndoFirst: () => boolean;
  drawEnterVertexEdit: () => boolean;
  /** Dashed lines/circles for snapped endpoints (visual only). */
  setSnapConnectionOverlays: (connections: SnapConnectionRecord[]) => void;
  drawReplaceGeometry: (geometry: GeoJSON.LineString | GeoJSON.Polygon) => boolean;
  placeContributionMarker: (lat: number, lng: number) => void;
  removeContributionMarker: () => void;
  highlightGeometry: (geojson: GeoJSON.Geometry) => void;
  clearHighlight: () => void;
  /** Dashed preview while contributing — point pin, line, or polygon boundary. */
  previewContributionGeometry: (geojson: GeoJSON.Geometry | null) => void;
  /** Reference carriageways kept visible while tracing divided highway pairs. */
  previewContributionGuideGeometries: (
    guides: Array<{ side: 'a' | 'b' | 'divide'; geometry: GeoJSON.LineString }> | null,
  ) => void;
  clearContributionPreview: () => void;
  /** Dashed amber lines — your pending road surveys (pre-tile). */
  setPendingSurveyGeometries: (collection: GeoJSON.FeatureCollection | null) => void;
  /** Campaign zone outlines + synced collection features (null removes that overlay). */
  setCampaignMapData: (opts: {
    zones: GeoJSON.FeatureCollection | null;
    live: GeoJSON.FeatureCollection | null;
    /** Fit this geometry instead of all campaign features (e.g. deep link to one zone). */
    focusGeometry?: GeoJSON.Geometry | null;
    /** When false, only update layers — no fitBounds. Defaults to true. */
    fitBounds?: boolean;
  }) => void;
  /** Re-run fitBounds for the last campaign extent (zones / focus zone / live) after the user pans away. */
  refitCampaignExtent: () => void;
  /** Fit the map to a geometry without reloading campaign layers (e.g. re-focus selected zone). */
  fitCampaignGeometry: (
    geometry: GeoJSON.Geometry | null | undefined,
    options?: { padding?: number; maxZoom?: number; duration?: number },
  ) => void;
}

interface MapContainerProps {
  mapStyle?: MapStyle;
  isTerrainEnabled?: boolean;
  isContributionMode?: boolean;
  isDrawingMode?: boolean;
  /** Line draw for roads/paths; polygon draw for area features. */
  drawLineMode?: boolean;
  tracePoints?: Array<{ lat: number; lng: number }>;
  /** When true, live GPS trace renders as a closed polygon perimeter. */
  traceRingMode?: boolean;
  /** Continuous walk vs manual corner capture. */
  traceCaptureMode?: TraceCaptureMode;
  traceVertexEditEnabled?: boolean;
  selectedTraceVertexIndex?: number | null;
  onTraceVertexSelect?: (index: number | null) => void;
  onContributionClick?: (location: { lat: number; lng: number }) => void;
  onDrawComplete?: (geometry: GeoJSON.Geometry) => void;
  onDrawGeometryChange?: (geometry: GeoJSON.Geometry) => void;
  onMapClick?: () => void;
  /** When set, clicks on campaign zone fills call this with the zone id (Feature.id or properties.zone_id). */
  onCampaignZoneClick?: (zoneId: string) => void;
  trafficSignalsEnabled?: boolean;
  /** Full layer visibility state from Layer Settings panel */
  layerSettings?: LayerSettings;
}

export const MapContainer = forwardRef<MapContainerRef, MapContainerProps>(
  function MapContainerInner({
    mapStyle = 'standard',
    isTerrainEnabled = true,
    isContributionMode = false,
    isDrawingMode = false,
    drawLineMode = false,
    tracePoints = [],
    traceRingMode = false,
    traceCaptureMode = 'continuous',
    traceVertexEditEnabled = false,
    selectedTraceVertexIndex = null,
    onTraceVertexSelect,
    onContributionClick,
    onDrawComplete,
    onDrawGeometryChange,
    onMapClick,
    onCampaignZoneClick,
    trafficSignalsEnabled = true,
    layerSettings = DEFAULT_LAYER_SETTINGS,
  }, ref) {
    const onCampaignZoneClickRef = useRef(onCampaignZoneClick);
    /** Map-level click — layer-specific clicks miss when synced captures render above zones. */
    const campaignZoneMapClickHandlerRef = useRef<((e: mapboxgl.MapMouseEvent) => void) | null>(
      null,
    );
    const campaignZoneEnterHandlerRef = useRef<(() => void) | null>(null);
    const campaignZoneLeaveHandlerRef = useRef<(() => void) | null>(null);

    useEffect(() => {
      onCampaignZoneClickRef.current = onCampaignZoneClick;
    }, [onCampaignZoneClick]);

    const { mapRef, mapContainerRef, setStyle } = useMapInitialization({
      mapStyle,
      isTerrainEnabled,
    });

    const {
      toggleLayer,
      applyBuildingLayers,
      toggle3D,
      toggleTerrain,
      setTerrainExaggeration,
    } = useMapLayers(mapRef);

    useMapOverlays(mapRef);
    useTrafficLayer(mapRef, trafficSignalsEnabled);
    useLiveTrafficLayer(mapRef, layerSettings.liveTraffic);
    useFloodExposureLayer(mapRef, layerSettings.floodExposure);
    useFloodExposureYearlyLayer(mapRef, layerSettings.floodExposureYears);
    useObservedWeatherLayers(mapRef, layerSettings);
    useForecastWeatherLayers(mapRef, layerSettings);
    useMotorwayJunctionLayer(mapRef);
    useCoordalCatalogIcons(mapRef, mapStyle);
    useLayerSettingsSync(mapRef, layerSettings, toggleLayer, applyBuildingLayers);

    const highlightMarkerRef = useRef<mapboxgl.Marker | null>(null);
    const previewMarkerRef = useRef<mapboxgl.Marker | null>(null);
    const lastCampaignFitBoundsRef = useRef<mapboxgl.LngLatBounds | null>(null);

    const { drawRef, placeMarkerAt, removeMarker, syncDrawGeometryToCallback } = useMapInteractions(mapRef, {
      isContributionMode,
      isDrawingMode,
      drawLineMode,
      tracePoints,
      traceRingMode,
      traceCaptureMode,
      traceVertexEditEnabled,
      selectedTraceVertexIndex,
      onTraceVertexSelect,
      onContributionClick,
      onDrawComplete,
      onDrawGeometryChange,
      onMapClick,
    });

    const clearContributionGuideLayers = (m: mapboxgl.Map) => {
      try { if (m.getLayer('contrib-guide-line')) m.removeLayer('contrib-guide-line'); } catch { /* noop */ }
      try { if (m.getSource('contrib-guide-src')) m.removeSource('contrib-guide-src'); } catch { /* noop */ }
    };

    const clearContributionPreviewLayers = (m: mapboxgl.Map) => {
      if (previewMarkerRef.current) {
        previewMarkerRef.current.remove();
        previewMarkerRef.current = null;
      }
      try { if (m.getLayer('contrib-preview-vertices')) m.removeLayer('contrib-preview-vertices'); } catch { /* noop */ }
      try { if (m.getLayer('contrib-preview-fill')) m.removeLayer('contrib-preview-fill'); } catch { /* noop */ }
      try { if (m.getLayer('contrib-preview-line')) m.removeLayer('contrib-preview-line'); } catch { /* noop */ }
      try { if (m.getSource('contrib-preview-src')) m.removeSource('contrib-preview-src'); } catch { /* noop */ }
      try { if (m.getSource('contrib-preview-vertices-src')) m.removeSource('contrib-preview-vertices-src'); } catch { /* noop */ }
    };

    const clearPendingSurveyLayers = (m: mapboxgl.Map) => {
      try { if (m.getLayer('pending-survey-endpoints')) m.removeLayer('pending-survey-endpoints'); } catch { /* noop */ }
      try { if (m.getLayer('pending-survey-line')) m.removeLayer('pending-survey-line'); } catch { /* noop */ }
      try { if (m.getSource('pending-survey-src')) m.removeSource('pending-survey-src'); } catch { /* noop */ }
      try { if (m.getSource('pending-survey-endpoints-src')) m.removeSource('pending-survey-endpoints-src'); } catch { /* noop */ }
    };

    const clearSnapConnectionLayers = (m: mapboxgl.Map) => {
      try { if (m.getLayer('snap-connection-line')) m.removeLayer('snap-connection-line'); } catch { /* noop */ }
      try { if (m.getLayer('snap-connection-point')) m.removeLayer('snap-connection-point'); } catch { /* noop */ }
      try { if (m.getSource('snap-connection-lines-src')) m.removeSource('snap-connection-lines-src'); } catch { /* noop */ }
      try { if (m.getSource('snap-connection-points-src')) m.removeSource('snap-connection-points-src'); } catch { /* noop */ }
    };

    useImperativeHandle(ref, () => ({
      setStyle,
      toggle3D,
      toggleTerrain,
      setTerrainExaggeration,
      toggleLayer,
      applyBuildingLayers,
      getMap: () => mapRef.current,
      getZoom: () => mapRef.current?.getZoom() || 0,
      getCenter: () => {
        const center = mapRef.current?.getCenter();
        return center ? { lng: center.lng, lat: center.lat } : { lng: 0, lat: 0 };
      },
      zoomIn: () => mapRef.current?.zoomIn({ duration: 300 }),
      zoomOut: () => mapRef.current?.zoomOut({ duration: 300 }),
      resetNorth: () => mapRef.current?.easeTo({ bearing: 0, pitch: 0, duration: 500 }),
      resetPitch: () => mapRef.current?.easeTo({ pitch: 0, duration: 500 }),
      toggleTilt: (tilted: boolean) => mapRef.current?.easeTo({ pitch: tilted ? 60 : 0, duration: 500 }),
      setPitch: (pitch: number) => mapRef.current?.easeTo({ pitch, duration: 500 }),
      getBearing: () => mapRef.current?.getBearing() || 0,
      getPitch: () => mapRef.current?.getPitch() || 0,
      flyTo: (center: { lng: number; lat: number }, zoom: number, options?: { bottomPadding?: number }) => {
        const map = mapRef.current;
        if (!map) return;
        const targetZoom = Math.max(map.getZoom(), zoom);
        const padding =
          options?.bottomPadding != null
            ? { top: 48, bottom: options.bottomPadding, left: 0, right: 0 }
            : undefined;
        map.flyTo({
          center: [center.lng, center.lat],
          zoom: targetZoom,
          padding,
          duration: 800,
        });
      },
      lockZoom: (_zoom?: number) => {
        const m = mapRef.current;
        if (!m) return;
        applySurveyMinZoom(m, SURVEY_MIN_ZOOM);
        m.scrollZoom.enable();
        m.doubleClickZoom.enable();
        m.touchZoomRotate.enable();
        m.dragPan.enable();
      },
      unlockZoom: () => {
        const m = mapRef.current;
        if (!m) return;
        m.setMinZoom(0);
        m.setMaxZoom(MAP_MAX_ZOOM);
        m.scrollZoom.enable();
        m.doubleClickZoom.enable();
        m.touchZoomRotate.enable();
        m.dragPan.enable();
      },
      drawTrash: () => {
        if (!drawRef.current) return false;
        const hasGeometry = trashDraw(drawRef.current, drawLineMode);
        if (hasGeometry) syncDrawGeometryToCallback();
        return hasGeometry;
      },
      drawUndo: () => {
        if (!drawRef.current) return false;
        const hasGeometry = undoDrawLastVertex(drawRef.current, drawLineMode);
        if (hasGeometry) syncDrawGeometryToCallback();
        return hasGeometry;
      },
      drawUndoFirst: () => {
        if (!drawRef.current) return false;
        const hasGeometry = undoDrawFirstVertex(drawRef.current, drawLineMode);
        if (hasGeometry) syncDrawGeometryToCallback();
        return hasGeometry;
      },
      drawEnterVertexEdit: () => {
        if (!drawRef.current) return false;
        return enterDrawDirectSelect(drawRef.current);
      },
      drawReplaceGeometry: (geometry: GeoJSON.LineString | GeoJSON.Polygon) => {
        if (!drawRef.current) return false;
        return replaceDrawGeometry(drawRef.current, geometry, drawLineMode);
      },
      placeContributionMarker: (lat: number, lng: number) => {
        placeMarkerAt(lat, lng);
      },
      removeContributionMarker: () => {
        removeMarker();
      },
      highlightGeometry: (geojson: GeoJSON.Geometry) => {
        const m = mapRef.current;
        if (!m) return;

        const applyLayers = () => {
          // Clear previous highlight
          if (highlightMarkerRef.current) { highlightMarkerRef.current.remove(); highlightMarkerRef.current = null; }
          try { if (m.getLayer('highlight-fill')) m.removeLayer('highlight-fill'); } catch { }
          try { if (m.getLayer('highlight-line')) m.removeLayer('highlight-line'); } catch { }
          try { if (m.getSource('highlight-src')) m.removeSource('highlight-src'); } catch { }

          if (geojson.type === 'Point') {
              const [lng, lat] = geojson.coordinates as [number, number];
              const el = document.createElement('div');
              el.innerHTML = `<div style="width:44px;height:44px;background:#10b981;border:3px solid white;border-radius:50%;box-shadow:0 2px 10px rgba(0,0,0,0.35);display:flex;align-items:center;justify-content:center;animation:pulse 1.5s ease-in-out infinite"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.5" stroke-linecap="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg></div>`;
              highlightMarkerRef.current = new mapboxgl.Marker({ element: el, anchor: 'center' })
                .setLngLat([lng, lat])
                .addTo(m);
            } else {
              const feature: GeoJSON.Feature = { type: 'Feature', properties: {}, geometry: geojson };
              m.addSource('highlight-src', { type: 'geojson', data: feature });
              if (geojson.type === 'Polygon' || geojson.type === 'MultiPolygon') {
                m.addLayer({
                  id: 'highlight-fill',
                  type: 'fill',
                  source: 'highlight-src',
                  slot: OVERLAY_FILL_SLOT,
                  paint: { 'fill-color': '#10b981', 'fill-opacity': 0.25 },
                });
                m.addLayer({
                  id: 'highlight-line',
                  type: 'line',
                  source: 'highlight-src',
                  slot: OVERLAY_LINE_SLOT,
                  layout: overlayLineLayout,
                  paint: overlayLinePaint({ 'line-color': '#10b981', 'line-width': 3 }),
                });
              } else {
                m.addLayer({
                  id: 'highlight-line',
                  type: 'line',
                  source: 'highlight-src',
                  slot: OVERLAY_LINE_SLOT,
                  layout: overlayLineLayout,
                  paint: overlayLinePaint({ 'line-color': '#10b981', 'line-width': 4 }),
                });
              }
            }
        };

        runWhenMapStyleReady(m, applyLayers);
      },
      clearHighlight: () => {
        const m = mapRef.current;
        removeMarker();
        if (highlightMarkerRef.current) { highlightMarkerRef.current.remove(); highlightMarkerRef.current = null; }
        if (!m) return;
        try { if (m.getLayer('highlight-fill')) m.removeLayer('highlight-fill'); } catch { }
        try { if (m.getLayer('highlight-line')) m.removeLayer('highlight-line'); } catch { }
        try { if (m.getSource('highlight-src')) m.removeSource('highlight-src'); } catch { }
      },
      previewContributionGeometry: (geojson: GeoJSON.Geometry | null) => {
        const m = mapRef.current;
        if (!m) return;
        clearContributionPreviewLayers(m);
        if (!geojson) return;

        const applyLayers = () => {
          clearContributionPreviewLayers(m);

          if (geojson.type === 'Point') {
            const [lng, lat] = geojson.coordinates as [number, number];
            const el = document.createElement('div');
            el.innerHTML = `<div style="width:36px;height:48px;filter:drop-shadow(0 2px 6px rgba(0,0,0,0.35))"><svg width="36" height="48" viewBox="0 0 36 48" fill="none"><path d="M18 0C9.716 0 3 6.716 3 15c0 10.5 15 33 15 33s15-22.5 15-33C33 6.716 26.284 0 18 0z" fill="#0d9488"/><circle cx="18" cy="15" r="6" fill="white"/></svg></div>`;
            previewMarkerRef.current = new mapboxgl.Marker({ element: el, anchor: 'bottom' })
              .setLngLat([lng, lat])
              .addTo(m);
            return;
          }

          const feature: GeoJSON.Feature = { type: 'Feature', properties: {}, geometry: geojson };
          m.addSource('contrib-preview-src', { type: 'geojson', data: feature });
          if (geojson.type === 'Polygon' || geojson.type === 'MultiPolygon') {
            m.addLayer({
              id: 'contrib-preview-fill',
              type: 'fill',
              source: 'contrib-preview-src',
              slot: OVERLAY_FILL_SLOT,
              paint: { 'fill-color': '#14b8a6', 'fill-opacity': 0.12 },
            });
          }
          m.addLayer({
            id: 'contrib-preview-line',
            type: 'line',
            source: 'contrib-preview-src',
            slot: OVERLAY_LINE_SLOT,
            layout: overlayLineLayout,
            paint: overlayLinePaint({
              'line-color': '#0d9488',
              'line-width': 3,
              'line-opacity': 0.95,
              'line-dasharray': [1.2, 1.2],
            }),
          });

          const vertexCoords =
            geojson.type === 'Polygon'
              ? ((geojson.coordinates[0] as number[][]).slice(0, -1))
              : geojson.type === 'LineString'
                ? (geojson.coordinates as number[][])
                : [];
          if (vertexCoords.length > 0) {
            const vertices: GeoJSON.FeatureCollection = {
              type: 'FeatureCollection',
              features: vertexCoords.map((c, i) => ({
                type: 'Feature',
                properties: { i },
                geometry: { type: 'Point', coordinates: c },
              })),
            };
            m.addSource('contrib-preview-vertices-src', { type: 'geojson', data: vertices });
            m.addLayer({
              id: 'contrib-preview-vertices',
              type: 'circle',
              source: 'contrib-preview-vertices-src',
              slot: OVERLAY_POINT_SLOT,
              paint: {
                'circle-radius': 4,
                'circle-color': '#0d9488',
                'circle-stroke-color': '#ffffff',
                'circle-stroke-width': 1.5,
              },
            });
          }
        };

        runWhenMapStyleReady(m, applyLayers);
      },
      previewContributionGuideGeometries: (
        guides: Array<{ side: 'a' | 'b' | 'divide'; geometry: GeoJSON.LineString }> | null,
      ) => {
        const m = mapRef.current;
        if (!m) return;

        const applyLayers = () => {
          clearContributionGuideLayers(m);
          if (!guides?.length) return;

          const collection: GeoJSON.FeatureCollection = {
            type: 'FeatureCollection',
            features: guides.map((guide) => ({
              type: 'Feature',
              properties: { carriageway_side: guide.side },
              geometry: guide.geometry,
            })),
          };
          m.addSource('contrib-guide-src', { type: 'geojson', data: collection });
          m.addLayer({
            id: 'contrib-guide-line',
            type: 'line',
            source: 'contrib-guide-src',
            slot: OVERLAY_LINE_SLOT,
            layout: overlayLineLayout,
            paint: overlayLinePaint({
              'line-color': [
                'match',
                ['get', 'carriageway_side'],
                'divide',
                '#eab308',
                'a',
                '#38bdf8',
                'b',
                '#fb923c',
                '#f8fafc',
              ],
              'line-width': [
                'match',
                ['get', 'carriageway_side'],
                'divide',
                3,
                2,
              ],
              'line-opacity': 0.95,
              'line-dasharray': [1.5, 1.8],
            }),
          });
        };

        runWhenMapStyleReady(m, applyLayers);
      },
      clearContributionPreview: () => {
        const m = mapRef.current;
        if (!m) return;
        clearContributionPreviewLayers(m);
        clearContributionGuideLayers(m);
      },
      setPendingSurveyGeometries: (collection: GeoJSON.FeatureCollection | null) => {
        const m = mapRef.current;
        if (!m) return;

        const applyLayers = () => {
          clearPendingSurveyLayers(m);
          if (!collection?.features?.length) return;

          m.addSource('pending-survey-src', {
            type: 'geojson',
            data: collection,
          });
          m.addLayer({
            id: 'pending-survey-line',
            type: 'line',
            source: 'pending-survey-src',
            slot: OVERLAY_LINE_SLOT,
            layout: overlayLineLayout,
            paint: overlayLinePaint({
              'line-color': '#f59e0b',
              'line-width': 4,
              'line-opacity': 0.92,
              'line-dasharray': [2, 1.5],
            }),
          });

          const endpointFeatures: GeoJSON.Feature[] = [];
          for (const feature of collection.features) {
            const geom = feature.geometry;
            if (geom.type !== 'LineString') continue;
            const coords = geom.coordinates as [number, number][];
            if (coords.length < 2) continue;
            endpointFeatures.push(
              {
                type: 'Feature',
                properties: { ...feature.properties, endpoint: 'start' },
                geometry: { type: 'Point', coordinates: coords[0] },
              },
              {
                type: 'Feature',
                properties: { ...feature.properties, endpoint: 'end' },
                geometry: { type: 'Point', coordinates: coords[coords.length - 1] },
              },
            );
          }
          if (endpointFeatures.length > 0) {
            m.addSource('pending-survey-endpoints-src', {
              type: 'geojson',
              data: { type: 'FeatureCollection', features: endpointFeatures },
            });
            m.addLayer({
              id: 'pending-survey-endpoints',
              type: 'circle',
              source: 'pending-survey-endpoints-src',
              slot: OVERLAY_POINT_SLOT,
              paint: {
                'circle-radius': 5,
                'circle-color': '#f59e0b',
                'circle-stroke-color': '#ffffff',
                'circle-stroke-width': 2,
              },
            });
          }
        };

        runWhenMapStyleReady(m, applyLayers);
      },
      setSnapConnectionOverlays: (connections: SnapConnectionRecord[]) => {
        const m = mapRef.current;
        if (!m) return;

        const applyLayers = () => {
          clearSnapConnectionLayers(m);
          if (!connections.length) return;

          const lineFeatures: GeoJSON.Feature[] = [];
          const pointFeatures: GeoJSON.Feature[] = [];

          for (const conn of connections) {
            pointFeatures.push({
              type: 'Feature',
              properties: { end: conn.end },
              geometry: {
                type: 'Point',
                coordinates: [conn.snap_lng, conn.snap_lat],
              },
            });
            if (
              conn.original_lng != null
              && conn.original_lat != null
              && (conn.original_lng !== conn.snap_lng || conn.original_lat !== conn.snap_lat)
            ) {
              lineFeatures.push({
                type: 'Feature',
                properties: {},
                geometry: {
                  type: 'LineString',
                  coordinates: [
                    [conn.original_lng, conn.original_lat],
                    [conn.snap_lng, conn.snap_lat],
                  ],
                },
              });
            }
          }

          if (lineFeatures.length > 0) {
            m.addSource('snap-connection-lines-src', {
              type: 'geojson',
              data: { type: 'FeatureCollection', features: lineFeatures },
            });
            m.addLayer({
              id: 'snap-connection-line',
              type: 'line',
              source: 'snap-connection-lines-src',
              slot: OVERLAY_LINE_SLOT,
              layout: overlayLineLayout,
              paint: overlayLinePaint({
                'line-color': '#2563eb',
                'line-width': 3,
                'line-opacity': 0.85,
                'line-dasharray': [2, 1.5],
              }),
            });
          }
          if (pointFeatures.length > 0) {
            m.addSource('snap-connection-points-src', {
              type: 'geojson',
              data: { type: 'FeatureCollection', features: pointFeatures },
            });
            m.addLayer({
              id: 'snap-connection-point',
              type: 'circle',
              source: 'snap-connection-points-src',
              slot: OVERLAY_POINT_SLOT,
              paint: {
                'circle-radius': 6,
                'circle-color': '#2563eb',
                'circle-stroke-color': '#ffffff',
                'circle-stroke-width': 2,
                'circle-opacity': 0.95,
              },
            });
          }
        };

        runWhenMapStyleReady(m, applyLayers);
      },
      refitCampaignExtent: () => {
        const m = mapRef.current;
        const b = lastCampaignFitBoundsRef.current;
        if (!m || !b || b.isEmpty()) return;
        m.fitBounds(b, { padding: 64, maxZoom: 15, duration: 750 });
      },
      fitCampaignGeometry: (
        geometry: GeoJSON.Geometry | null | undefined,
        options?: { padding?: number; maxZoom?: number; duration?: number },
      ) => {
        const m = mapRef.current;
        if (!m || !geometry) return;
        const fb = new mapboxgl.LngLatBounds();
        if (geometry.type === 'GeometryCollection') {
          for (const part of geometry.geometries) {
            extendBoundsFromGeometry(fb, part);
          }
        } else {
          extendBoundsFromGeometry(fb, geometry);
        }
        if (fb.isEmpty()) return;
        lastCampaignFitBoundsRef.current = fb;
        m.fitBounds(fb, {
          padding: options?.padding ?? 72,
          maxZoom: options?.maxZoom ?? 16,
          duration: options?.duration ?? 900,
        });
      },
      setCampaignMapData: (opts: {
        zones: GeoJSON.FeatureCollection | null;
        live: GeoJSON.FeatureCollection | null;
        focusGeometry?: GeoJSON.Geometry | null;
        fitBounds?: boolean;
      }) => {
        const m = mapRef.current;
        if (!m) return;
        const allowFit = opts.fitBounds !== false;
        const rmLayer = (id: string) => {
          try {
            if (m.getLayer(id)) m.removeLayer(id);
          } catch { /* noop */ }
        };
        const rmSource = (id: string) => {
          try {
            if (m.getSource(id)) m.removeSource(id);
          } catch { /* noop */ }
        };

        if (campaignZoneMapClickHandlerRef.current) {
          try {
            m.off('click', campaignZoneMapClickHandlerRef.current);
          } catch { /* noop */ }
          campaignZoneMapClickHandlerRef.current = null;
        }
        if (campaignZoneEnterHandlerRef.current) {
          try {
            m.off('mouseenter', 'campaign-zones-fill', campaignZoneEnterHandlerRef.current);
          } catch { /* noop */ }
          campaignZoneEnterHandlerRef.current = null;
        }
        if (campaignZoneLeaveHandlerRef.current) {
          try {
            m.off('mouseleave', 'campaign-zones-fill', campaignZoneLeaveHandlerRef.current);
          } catch { /* noop */ }
          campaignZoneLeaveHandlerRef.current = null;
        }

        rmLayer('campaign-zones-fill');
        rmLayer('campaign-zones-line');
        rmLayer('campaign-live-circle');
        rmLayer('campaign-live-fill');
        rmLayer('campaign-live-line');
        rmSource('campaign-zones');
        rmSource('campaign-live');

        const bounds = new mapboxgl.LngLatBounds();

        if (opts.zones && opts.zones.features?.length) {
          m.addSource('campaign-zones', { type: 'geojson', data: opts.zones });
          m.addLayer({
            id: 'campaign-zones-fill',
            type: 'fill',
            source: 'campaign-zones',
            paint: {
              'fill-color': [
                'match',
                ['get', 'status'],
                'assigned', '#3b82f6',
                'in_progress', '#f59e0b',
                'completed', '#22c55e',
                'needs_review', '#8b5cf6',
                '#94a3b8',
              ],
              'fill-opacity': 0.18,
            },
          });
          m.addLayer({
            id: 'campaign-zones-line',
            type: 'line',
            source: 'campaign-zones',
            paint: {
              'line-color': [
                'match',
                ['get', 'status'],
                'assigned', '#3b82f6',
                'in_progress', '#f59e0b',
                'completed', '#22c55e',
                'needs_review', '#8b5cf6',
                '#94a3b8',
              ],
              'line-width': 2,
              'line-opacity': 0.9,
            },
          });
          for (const f of opts.zones.features) {
            if (f.geometry) extendBoundsFromGeometry(bounds, f.geometry);
          }

          if (onCampaignZoneClickRef.current) {
            const onEnter = () => {
              m.getCanvas().style.cursor = 'pointer';
            };
            const onLeave = () => {
              m.getCanvas().style.cursor = '';
            };
            campaignZoneEnterHandlerRef.current = onEnter;
            campaignZoneLeaveHandlerRef.current = onLeave;
            m.on('mouseenter', 'campaign-zones-fill', onEnter);
            m.on('mouseleave', 'campaign-zones-fill', onLeave);

            const onMapZoneClick = (e: mapboxgl.MapMouseEvent) => {
              const layerIds = ['campaign-zones-fill', 'campaign-zones-line'].filter((id) => {
                try {
                  return Boolean(m.getLayer(id));
                } catch {
                  return false;
                }
              });
              if (!layerIds.length) return;
              const feats = m.queryRenderedFeatures(e.point, { layers: layerIds });
              if (!feats.length) return;
              const feat = feats[0];
              const zid =
                feat.id != null && feat.id !== ''
                  ? String(feat.id)
                  : String((feat.properties as { zone_id?: string } | null)?.zone_id ?? '');
              if (zid && onCampaignZoneClickRef.current) {
                e.originalEvent?.stopPropagation?.();
                onCampaignZoneClickRef.current(zid);
              }
            };
            campaignZoneMapClickHandlerRef.current = onMapZoneClick;
            m.on('click', onMapZoneClick);
          }
        }

        if (opts.live && opts.live.features?.length) {
          m.addSource('campaign-live', { type: 'geojson', data: opts.live });
          m.addLayer({
            id: 'campaign-live-circle',
            type: 'circle',
            source: 'campaign-live',
            filter: ['==', ['geometry-type'], 'Point'],
            paint: {
              'circle-radius': 6,
              'circle-color': [
                'match',
                ['get', 'status'],
                'approved', '#22c55e',
                'rejected', '#ef4444',
                '#f59e0b',
              ],
              'circle-opacity': 0.92,
              'circle-stroke-width': 1.5,
              'circle-stroke-color': '#0f172a',
            },
          });
          m.addLayer({
            id: 'campaign-live-fill',
            type: 'fill',
            source: 'campaign-live',
            filter: ['in', ['geometry-type'], ['literal', ['Polygon', 'MultiPolygon']]],
            paint: {
              'fill-color': [
                'match',
                ['get', 'status'],
                'approved', '#22c55e',
                'rejected', '#ef4444',
                '#38bdf8',
              ],
              'fill-opacity': 0.2,
            },
          });
          m.addLayer({
            id: 'campaign-live-line',
            type: 'line',
            source: 'campaign-live',
            filter: ['in', ['geometry-type'], ['literal', ['LineString', 'MultiLineString', 'Polygon', 'MultiPolygon']]],
            paint: {
              'line-color': [
                'match',
                ['get', 'status'],
                'approved', '#22c55e',
                'rejected', '#ef4444',
                '#38bdf8',
              ],
              'line-width': 2,
              'line-opacity': 0.75,
            },
          });
          for (const f of opts.live.features) {
            if (f.geometry) extendBoundsFromGeometry(bounds, f.geometry);
          }
        }

        const focus = opts.focusGeometry;
        let refitBounds: mapboxgl.LngLatBounds | null = null;
        if (focus && focus.type !== 'GeometryCollection') {
          const rb = new mapboxgl.LngLatBounds();
          extendBoundsFromGeometry(rb, focus);
          if (!rb.isEmpty()) refitBounds = rb;
        } else if (!bounds.isEmpty()) {
          refitBounds = bounds;
        }
        lastCampaignFitBoundsRef.current = refitBounds;

        if (
          allowFit &&
          focus &&
          focus.type !== 'GeometryCollection'
        ) {
          const fb = new mapboxgl.LngLatBounds();
          extendBoundsFromGeometry(fb, focus);
          if (!fb.isEmpty()) {
            m.fitBounds(fb, { padding: 72, maxZoom: 16, duration: 900 });
          }
        } else if (allowFit && !bounds.isEmpty()) {
          m.fitBounds(bounds, { padding: 56, maxZoom: 14, duration: 900 });
        }
      },
    }));

    return (
      <>
        <div ref={mapContainerRef} data-testid="map-container" className="absolute inset-0" />
        <style>{`
          .custom-marker { cursor: pointer; pointer-events: auto; }
          .search-marker {
            position: relative;
            width: 0;
            height: 0;
            overflow: visible;
            pointer-events: none;
          }
          .marker-pin {
            position: absolute;
            left: 4px;
            bottom: -1px;
            width: 30px;
            height: 30px;
            border-radius: 50% 50% 50% 0;
            background: linear-gradient(135deg, #10b981 0%, #059669 100%);
            transform: rotate(-45deg);
            box-shadow: 0 3px 12px rgba(5,150,105,0.4), 0 1px 3px rgba(0,0,0,0.15);
            border: 2.5px solid white;
            transition: all 0.2s ease;
            pointer-events: auto;
          }
          .marker-pin::after {
            content: '';
            position: absolute;
            width: 8px;
            height: 8px;
            background: white;
            border-radius: 50%;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%) rotate(45deg);
            box-shadow: inset 0 1px 2px rgba(0,0,0,0.1);
          }
          .marker-pin.selected {
            background: linear-gradient(135deg, #059669 0%, #047857 100%);
            border-color: #d1fae5;
            transform: rotate(-45deg) scale(1.12);
            box-shadow: 0 4px 16px rgba(5,150,105,0.5), 0 0 0 3px rgba(16,185,129,0.25);
          }
          .marker-pin.selected::after {
            background: #d1fae5;
          }
          /* Hide MapboxDraw default toolbar — we use custom controls */
          .mapboxgl-ctrl-group.mapbox-gl-draw_ctrl-draw-btn,
          .mapbox-gl-draw_ctrl-draw-btn,
          .mapboxgl-ctrl-top-right .mapboxgl-ctrl-group { display: none !important; }
          /* MapboxDraw mounts an invisible top-right control — don't steal map clicks/cursor */
          .mapboxgl-ctrl-top-right,
          .mapbox-gl-draw,
          .mapbox-gl-draw_ctrl-draw { pointer-events: none !important; }
        `}</style>
      </>
    );
  }
);
